import { useState } from "react";
import { Loader2, Lock } from "lucide-react";
import LogoMark from "@/components/LogoMark";

const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

const inputCls =
  "w-full border border-bone/20 bg-ink px-4 py-3 text-base text-bone placeholder:text-bone/40 outline-none transition-colors duration-200 focus:border-steel";

export default function AdminLogin({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/login`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ email, password }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiErrorDetail(data.detail));
      onSuccess();
    } catch (err) {
      setError(err.message || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-5">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-sm border border-bone/10 bg-charcoal p-8 sm:p-10"
      >
        <div className="flex items-center justify-center">
          <LogoMark className="h-14" />
        </div>
        <h1 className="mt-6 text-center font-display text-4xl font-black uppercase tracking-[0.02em] text-bone">
          Owner Login
        </h1>
        <p className="mt-2 text-center text-sm text-bone/60">
          Requests dashboard — Dreamworlds Garage
        </p>

        <label className="mt-8 block">
          <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-bone/70">
            Email
          </span>
          <input
            required
            type="email"
            data-testid="admin-login-email-input"
            className={inputCls}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="dreamworldsgarage@gmail.com"
          />
        </label>
        <label className="mt-5 block">
          <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-bone/70">
            Password
          </span>
          <input
            required
            type="password"
            data-testid="admin-login-password-input"
            className={inputCls}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Your password"
          />
        </label>

        {error && (
          <p
            data-testid="admin-login-error"
            className="mt-4 border border-red-900/50 bg-red-950/40 px-4 py-3 text-sm text-red-300"
          >
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={busy}
          data-testid="admin-login-submit-button"
          className="mt-8 flex w-full items-center justify-center gap-3 bg-navy px-6 py-3.5 font-display text-xl font-bold uppercase tracking-wide text-white transition-colors duration-200 hover:bg-bone hover:text-ink disabled:opacity-60"
        >
          {busy ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <Lock className="h-5 w-5" />
          )}
          {busy ? "Checking..." : "Sign In"}
        </button>
        <p className="mt-6 text-center text-xs uppercase tracking-[0.2em] text-bone/40">
          Authorized access only
        </p>
      </form>
    </div>
  );
}
